import axios from "axios";

import { IHttpClientInstance, IHttpClientRequestConfig, IHttpClientToken, IHttpClientResponse } from "./types";

export class HttpClient {
    protected instance!: IHttpClientInstance;
    protected token!: IHttpClientToken;

    constructor(baseURL: string, config?: IHttpClientRequestConfig) {
        if (config && config.token) this.token = config.token;
        this.createInstance(baseURL, config);
    }

    protected createInstance(baseURL: string, config?: IHttpClientRequestConfig): void {
        this.instance = axios.create({ ...config, baseURL });
        this.instance.defaults.timeout = 60 * 1000; // Now all requests will wait 1 minute before timing out
        this.instance.defaults.headers["content-type"] = "application/json";
        this.instance.defaults.responseType = "json";
    }

    public setRequestInterceptor(success: any, error: any): void {
        this.instance.interceptors.request.use(success, error);
    }

    public setResponseInterceptor(success: any, error: any): void {
        this.instance.interceptors.response.use(success, error);
    }

    public setToken(token: string | (() => any)): void {
        if (this.token) this.token.token = token;
    }

    public async get<T = any, R = IHttpClientResponse<T>>(url: string, data?: any, config?: IHttpClientRequestConfig): Promise<R> {
        const newConfig: any = { params: (data || {}), ...config ? this.setConfig(config) : {} };
        return this.instance.get<T, R>(url, newConfig);
    }

    public async post<T = any, R = IHttpClientResponse<T>>(url: string, data?: any, config?: IHttpClientRequestConfig): Promise<R> {
        return this.instance.post<T, R>(url, data, config ? this.setConfig(config) : undefined);
    }

    public async put<T = any, R = IHttpClientResponse<T>>(url: string, data?: any, config?: IHttpClientRequestConfig): Promise<R> {
        return this.instance.put<T, R>(url, data, config ? this.setConfig(config) : undefined);
    }

    public async delete<T = any, R = IHttpClientResponse<T>>(url: string, data?: any, config?: IHttpClientRequestConfig): Promise<R> {
        const newConfig: any = { params: (data || {}), ...config ? this.setConfig(config) : {} };
        return this.instance.delete<T, R>(url, newConfig);
    }

    private setConfig(config: IHttpClientRequestConfig): IHttpClientRequestConfig {
        if (!config) return config;

        // Token configuration on header
        const isAuthenticate: boolean = config.hasOwnProperty("isAuthenticate") ? (config.isAuthenticate || false) : true;
        if (isAuthenticate && this.token.name) {
            const token: IHttpClientToken = config.token ? config.token : this.token;
            const tokenHeaders: { [name: string]: string } = { [token.name]: `${token.type || ""} ${token.token}` };
            return { ...config, headers: { ...config.headers, ...tokenHeaders } };
        }
        return config;
    }
}
